import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  Calendar,
  CalendarDays,
  Users,
  MessageCircle,
  Lightbulb,
  Briefcase,
  Map,
  User,
  Settings,
  ChevronLeft,
  ChevronRight,
  Shield,
  ClipboardCheck,
  BarChart3,
  Database,
  Bell,
  Building2,
  Megaphone,
  Printer,
} from 'lucide-react';
import { cn, displayName, getInitials } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useDesktop } from '@/contexts/DesktopContext';
import { useUnreadCount } from '@/hooks/useUnreadCount';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

const mainNavItems: NavItem[] = [
  { title: 'Home', href: '/', icon: Home },
  { title: 'Schedule', href: '/schedule', icon: Calendar },
  { title: 'Network', href: '/network', icon: Users },
  { title: 'Messages', href: '/messages', icon: MessageCircle },
  { title: 'Opportunities', href: '/opportunities', icon: Lightbulb },
  { title: 'Industry Partners', href: '/industry-partners', icon: Briefcase },
  { title: 'Event Map', href: '/event-map', icon: Map },
];

const adminNavItems: NavItem[] = [
  { title: 'Events', href: '/admin/events', icon: CalendarDays },
  { title: 'Sponsors', href: '/admin/sponsors', icon: Megaphone },
  { title: 'Sessions', href: '/admin/sessions', icon: Calendar },
  { title: 'Network', href: '/admin/network', icon: Users },
  { title: 'Opportunities', href: '/admin/opportunities', icon: Briefcase },
  { title: 'Exhibitors / Partners', href: '/admin/partners', icon: Building2 },
  { title: 'Data and Reporting', href: '/admin/data', icon: Database },
  { title: 'Notifications', href: '/admin/notifications', icon: Bell },
  { title: 'Badge Export', href: '/admin/badges', icon: Printer },
  { title: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
];


export function EnhancedSidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const { sidebarCollapsed, toggleSidebar, openProfileModal, openSettingsModal } = useDesktop();
  const { unreadCount } = useUnreadCount();

  const isAdmin = user?.roles?.includes('admin') || false;
  const isStaff = user?.roles?.includes('staff') || false;

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const NavLink = ({ item }: { item: NavItem }) => {
    const active = isActive(item.href);
    const Icon = item.icon;
    const hasBadge = item.badge && item.badge > 0;

    const linkContent = (
      <Link
        to={item.href}
        className={cn(
          'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors relative',
          active
            ? 'bg-primary/10 text-primary font-medium border border-primary/20'
            : 'text-muted-foreground hover:bg-card hover:text-foreground',
          sidebarCollapsed && 'justify-center px-2'
        )}
      >
        <div className="relative">
          <Icon className={cn('h-5 w-5 flex-shrink-0', active && 'text-primary')} />
          {sidebarCollapsed && hasBadge && (
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-primary rounded-full border-2 border-muted" />
          )}
        </div>
        {!sidebarCollapsed && (
          <span className="text-sm">
            {item.title}
          </span>
        )}
        {!sidebarCollapsed && hasBadge && (
          <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-primary text-primary-foreground font-medium">
            {item.badge}
          </span>
        )}
      </Link>
    );

    if (sidebarCollapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
          <TooltipContent side="right" sideOffset={10}>
            <div className="flex items-center gap-2">
              <span>{item.title}</span>
              {hasBadge && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground font-medium">
                  {item.badge}
                </span>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      );
    }

    return linkContent;
  };

  return (
    <TooltipProvider>
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? 72 : 256 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className={cn(
          'h-full flex-shrink-0 relative',
          'bg-muted border-r border-border',
          'flex flex-col'
        )}
      >
        {/* Main Navigation */}
        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
          {mainNavItems.map((item) => {
            // Add unread count badge to Messages nav item
            const itemWithBadge = item.href === '/messages' && unreadCount > 0
              ? { ...item, badge: unreadCount }
              : item;
            return <NavLink key={item.href} item={itemWithBadge} />;
          })}

          {/* Staff Check-in */}
          {(isStaff || isAdmin) && (
            <>
              <div className={cn(
                'my-2 border-t border-border',
                sidebarCollapsed && 'mx-2'
              )} />
              <NavLink
                item={{
                  title: 'Staff Check-In',
                  href: '/staff/checkin',
                  icon: ClipboardCheck,
                }}
              />
            </>
          )}

          {/* Admin Section */}
          {isAdmin && (
            <>
              <div className={cn(
                'my-2 border-t border-border',
                sidebarCollapsed && 'mx-2'
              )} />
              {sidebarCollapsed ? (
                <NavLink
                  item={{
                    title: 'Admin',
                    href: '/admin',
                    icon: Shield,
                  }}
                />
              ) : (
                <>
                  <div className="px-3 py-1">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <Shield className="h-3 w-3" />
                      Admin
                    </p>
                  </div>
                  {adminNavItems.map((item) => {
                    const active = item.href === '/admin'
                      ? location.pathname === '/admin'
                      : location.pathname.startsWith(item.href);
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={cn(
                          'flex items-center gap-3 pl-6 pr-3 py-1.5 rounded-lg transition-colors text-sm',
                          active
                            ? 'bg-primary/10 text-primary font-medium border border-primary/20'
                            : 'text-muted-foreground hover:bg-card hover:text-foreground'
                        )}
                      >
                        <Icon className={cn('h-4 w-4 flex-shrink-0', active && 'text-primary')} />
                        {item.title}
                      </Link>
                    );
                  })}
                </>
              )}
            </>
          )}
        </nav>

        {/* Bottom Section */}
        <div className="border-t border-border">
          {/* Profile & Settings Buttons */}
          <div className="px-3 py-1.5 space-y-0.5">
            {/* Profile Button */}
            {sidebarCollapsed ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    onClick={openProfileModal}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors w-full',
                      'text-muted-foreground hover:bg-card hover:text-foreground',
                      'justify-center px-2'
                    )}
                  >
                    <User className="h-5 w-5 flex-shrink-0" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={10}>
                  Profile
                </TooltipContent>
              </Tooltip>
            ) : (
              <button
                onClick={openProfileModal}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors w-full',
                  'text-muted-foreground hover:bg-card hover:text-foreground'
                )}
              >
                <User className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">Profile</span>
              </button>
            )}

            {/* Settings Button */}
            {sidebarCollapsed ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    onClick={openSettingsModal}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors w-full',
                      'text-muted-foreground hover:bg-card hover:text-foreground',
                      'justify-center px-2'
                    )}
                  >
                    <Settings className="h-5 w-5 flex-shrink-0" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={10}>
                  Settings
                </TooltipContent>
              </Tooltip>
            ) : (
              <button
                onClick={openSettingsModal}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors w-full',
                  'text-muted-foreground hover:bg-card hover:text-foreground'
                )}
              >
                <Settings className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">Settings</span>
              </button>
            )}
          </div>

          {/* User Info */}
          <div className={cn(
            'px-3 py-2 border-t border-border',
            sidebarCollapsed && 'px-2'
          )}>
            {!sidebarCollapsed ? (
              <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user?.avatarUrl || undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {user ? getInitials(displayName(user)) : 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate text-foreground">{user ? displayName(user) : 'User'}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
            ) : (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div className="flex justify-center">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user?.avatarUrl || undefined} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                        {user ? getInitials(displayName(user)) : 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={10}>
                  {user ? displayName(user) : 'User'}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

      </motion.aside>

      {/* Collapse Toggle - Outside aside to avoid overflow issues */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleSidebar}
        className={cn(
          'absolute z-50',
          'w-6 h-6 rounded-full',
          'bg-card border border-border shadow-sm',
          'hover:bg-muted text-muted-foreground'
        )}
        style={{
          top: '52px',
          left: sidebarCollapsed ? 60 : 244,
          transition: 'left 0.2s ease-in-out',
        }}
      >
        {sidebarCollapsed ? (
          <ChevronRight className="h-3 w-3" />
        ) : (
          <ChevronLeft className="h-3 w-3" />
        )}
      </Button>
    </TooltipProvider>
  );
}
